/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { getLanguageMatch } from 'js/lib/language';
import { languageTagToLocalName } from 'js/lib/languageLocalized';

import { Typography2 } from '@coursera/cds-core';
import { CheckIcon } from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import type { Language } from 'bundles/page-header/components/translation/useCourseLanguageQuery';
import { useSetUserLocaleMutation } from 'bundles/page-header/components/translation/useSetUserLocaleMutation';

import _t from 'i18n!nls/composable-page-header';

const styles = {
  languageItem: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--cds-spacing-100);
    padding: var(--cds-spacing-100) var(--cds-spacing-150);
    border-radius: var(--cds-border-radius-50);

    &:hover {
      background-color: var(--cds-color-interactive-background-primary-hover-weak);
    }
  `,
  selectedLanguageItem: css`
    background-color: var(--cds-color-interactive-background-primary-hover-weak);
  `,
  label: css`
    display: flex;
    align-items: center;
    gap: 8px;
  `,
};

type Props = {
  language: Language;
  currentLanguage: string;
};

const TranslationMenuItem = ({ language, currentLanguage }: Props) => {
  const track = useTracker();
  const setUserLocale = useSetUserLocaleMutation();
  const isSelected = !!getLanguageMatch(language.key, currentLanguage);

  const handleClick = async () => {
    track('switch_language', {
      previousLanguage: currentLanguage,
      currentLanguage: language.key,
    });

    await setUserLocale(language.key);
    location.reload();
  };

  return (
    <div
      role="option"
      aria-selected={isSelected}
      tabIndex={0}
      css={[styles.languageItem, isSelected && styles.selectedLanguageItem]}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <div css={styles.label}>
        <Typography2 variant="bodyPrimary" component="div">
          {languageTagToLocalName(language.key)}
        </Typography2>
        {language.isOriginal && (
          <Typography2 variant="bodyPrimary" color="supportText" component="div">
            ({_t('original')})
          </Typography2>
        )}
      </div>
      {isSelected && <CheckIcon size="small" color="interactive" />}
    </div>
  );
};

export default TranslationMenuItem;
